import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { NumeratorsCrumbs } from '../constants';
import FiltersNumerators from 'system/components/numerators/list/filter-functions';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useFormik } from 'formik';
import Table from 'shell/components/shared/table';
import { Numerator as NumeratorVM, FiltersFormNumerators } from './types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { HttpResponseList } from 'shell/models/http-response';
import { __OPERATION__MANAGEMENT__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { Numerator } from 'shell/models/numerator';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, validationSchemaNumeratorsFilter, headCells } from './constants';
import ListHeader from 'shell/components/shared/list-header';
import { Typography } from '@mui/material';

const initialValues: FiltersFormNumerators = {
  numeratorRegisterType: '',
  year: '',
  description: '',
  orders: {},
  page: 0,
  rowsPerPage: 10,
};

const NumeratorsListPage = (): React.ReactElement => {
  const navigate = useNavigate();

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<NumeratorVM>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<NumeratorVM>>({
    element: null,
    open: false,
  });

  const { delete: deleteNumerator } = useHttpFetch(
    `${__OPERATION__MANAGEMENT__}/numeratore/documenti`
  );

  const {
    data,
    get: getNumerators,
    error,
  } = useHttpFetch<HttpResponseList<Numerator>>(
    `${__OPERATION__MANAGEMENT__}/numeratore/documenti/`,
    {
      data: {
        payload: {
          pageInfo: {
            totalCount: 0,
          },
          list: [],
        },
      },
    }
  );

  const numerators: Array<NumeratorVM> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((numerator) => ({
      entityCode: numerator.key.codiceEnte,
      numeratorRegisterType: numerator.key.tipoRegistro,
      year: numerator.key.annoDocumento.toString(),
      description: numerator.descrizioneNumeratore?.toString() || '',
      currentNumerator: numerator.progressivoDocumento?.toString() || '',
    }));
  }, [data, error]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.list) {
      return 0;
    }
    return data.payload.list.length;
  }, [error, data?.payload]);

  const onSubmit = useCallback(
    (formValues: FiltersFormNumerators) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterNumerators.setFieldValue('page', 0);
      getNumerators(queryString);
    },
    [getNumerators]
  );

  const filterNumerators = useFormik({
    initialValues,
    validationSchema: validationSchemaNumeratorsFilter,
    onSubmit,
  });

  const filterTable = useFilterTable<FiltersFormNumerators>();

  useEffect(() => {
    filterNumerators.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(filterTable.values || initialValues);
    getNumerators(queryString);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterNumerators.values);
  }, [filterNumerators.values]);

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell: NumeratorVM): Promise<NumeratorVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/numeratori/dettaglio/:codiceEnte/:tipoRegistro/:annoDocumento', {
            codiceEnte: cell.entityCode || '',
            tipoRegistro: cell.numeratorRegisterType,
            annoDocumento: cell.year,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell: NumeratorVM): Promise<NumeratorVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/numeratori/modifica/:codiceEnte/:tipoRegistro/:annoDocumento', {
            codiceEnte: cell.entityCode || '',
            tipoRegistro: cell.numeratorRegisterType,
            annoDocumento: cell.year,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<NumeratorVM> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<NumeratorVM>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterNumerators.setFieldValue('page', newPage);
    },
    [filterNumerators.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterNumerators.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterNumerators.setValues(newDataset);
    },
    [filterNumerators.setFieldValue, filterNumerators.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterNumerators.values, orders };

      const queryString = transformDataset(newDataset);
      return getNumerators(queryString).then(() => {
        filterNumerators.setFieldValue('orders', orders);
      });
    },
    [filterNumerators.setFieldValue, getNumerators, filterNumerators.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={NumeratorsCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo numeratore"
        addBtnLabel="Aggiungi nuovo numeratore"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/numeratori/nuovo');
        }}
        title="Gestione Numeratori"
      />
      <div className="pmi-my-3">
        <FiltersNumerators filterNumeratorsForm={filterNumerators} />
      </div>
      {data?.payload ? (
        numerators?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterNumerators.values.orders}
            activeActions={auths}
            page={filterNumerators.values.page || 0}
            rowsPerPage={filterNumerators.values.rowsPerPage}
            body={numerators}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Numeratori"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione numeratore"
        message={`Confermare la cancellazione del numeratore ${deleteAlertModal.element?.numeratorRegisterType} (${deleteAlertModal.element?.year}) selezionato?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteNumerator(
            `/${deleteAlertModal.element?.entityCode}/${deleteAlertModal.element?.numeratorRegisterType}/${deleteAlertModal.element?.year}`
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione numeratore"
        message={`La cancellazione del numeratore ${deleteSuccessModal.element?.numeratorRegisterType} (${deleteSuccessModal.element?.year}) è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterNumerators.values, page: 0 });
          getNumerators(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterNumerators.values, page: 0 });
          getNumerators(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

NumeratorsListPage.displayName = 'Numerators list page component';

export default NumeratorsListPage;
